import React from "react";
// UI Framework
import { TableCell } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
// Common Components
import {
  Modal,
  Button,
  Loading,
  FullScreenLoading,
  StaticDropdown,
} from "src/Components";
// Local Components
import AddLiquidityModal from "./AddLiquidityModal";
import LiquidityManagementRecordsTable from "./LiquidityManagementRecordsTable";
// Services
import {
  deleteLiquidityManagementTask,
  withdrawEnable,
  withdrawDisabled,
  withdrawRequest,
  bulkCancel,
} from "src/Requests/imePortfolio";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
// Types
import {
  IAdditional,
  IAdditionalHeaderProps,
} from "src/Tables/TableTemplate/tableTemplate";
import { ILiquidityManagementTableReport } from "src/Interfaces/imePortfolio";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";

enum Action {
  OnWithdraw,
  OffWithdraw,
  None,
}

const AdditionalHeader = ({
  selectedRowsIds,
  setSelectedRowsIds,
  onAction,
  isSelectAll,
}: IAdditionalHeaderProps) => {
  // local store
  const [loading, setLoading] = React.useState(false);
  const [selectedAction, setSelectedAction] = React.useState<Action>(
    Action.None,
  );

  const actionOptions: DropdownItem[] = [
    { id: Action.OnWithdraw, name: "روشن کردن برداشت اتوماتیک از اختیار" },
    { id: Action.OffWithdraw, name: "خاموش کردن برداشت اتوماتیک از اختیار" },
  ];

  const getBulkActionFunction = () => {
    switch (selectedAction) {
      case Action.OnWithdraw:
        return withdrawEnable;
      case Action.OffWithdraw:
        return withdrawDisabled;
      default:
        return null;
    }
  };
  const bulkActionsHandler = () => {
    if (selectedAction === Action.None) return;
    if (!selectedRowsIds || !selectedRowsIds.length) return;
    const actionFunction = getBulkActionFunction();
    if (!actionFunction) return;
    setLoading(true);
    actionFunction(selectedRowsIds)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const bulkCancelOrderHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    if (!selectedRowsIds.length) return;
    bulkCancel(selectedRowsIds)
      .then((resp) => {
        onAction();
        toast.success(resp.msg);
        setSelectedRowsIds([]);
      })
      .catch(packetErrorHandler);
  };

  return (
    <>
      <TableCell>
        {loading ? <FullScreenLoading /> : <></>}
        <div className="w-[350px] flex gap-2">
          <StaticDropdown
            dropdown={{
              name: "",
              placeholder: "عملیات گروهی",
            }}
            onChange={(_, v) => setSelectedAction(v)}
            options={actionOptions}
            value={selectedAction}
          />
          <Button
            text="اعمال"
            onClick={bulkActionsHandler}
            variant="outlined"
            disabled={
              selectedAction === Action.None ||
              !selectedRowsIds ||
              !selectedRowsIds.length
            }
            className="!w-[50px] table-btn m-10"
          />
        </div>
      </TableCell>
      <TableCell>
        <div
          className={`transition-all rounded p-1 ${
            selectedRowsIds.length
              ? "hover:bg-red-100 cursor-pointer"
              : "cursor-not-allowed"
          }`}
          onClick={selectedRowsIds.length ? bulkCancelOrderHandler : undefined}
          aria-disabled={!selectedRowsIds.length}
        >
          <DeleteIcon
            fontSize="small"
            className={`cursor-pointer ${
              selectedRowsIds.length ? "text-red-900 " : "text-gray-300"
            } transition-all`}
          />
        </div>
      </TableCell>
      <TableCell />
      <TableCell />
    </>
  );
};

const AdditionalCol: React.FC<{
  row: ILiquidityManagementTableReport;
  onAction: () => void;
}> = ({ row, onAction }) => {
  // local store
  const [loading, setLoading] = React.useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = React.useState(false);

  const deleteHandler = () => {
    setLoading(true);
    deleteLiquidityManagementTask(row.id)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <>
      <TableCell>
        {isEditModalOpen ? (
          <Modal
            title="ویرایش نقدینگی"
            UI={<AddLiquidityModal row={row} onSuccess={onAction} />}
            onClose={() => setIsEditModalOpen(false)}
            modalBoxClassName="!min-w-[360px]"
          />
        ) : null}
        {isLogsModalOpen ? (
          <Modal
            title="سوابق نقدینگی"
            UI={<LiquidityManagementRecordsTable taskId={row.id} />}
            onClose={() => setIsLogsModalOpen(false)}
            modalBoxClassName="!max-w-[95vw] !w-[1200px]"
          />
        ) : null}

        <div className="w-16 mx-auto" onClick={() => setIsEditModalOpen(true)}>
          <EditIcon className="text-sky-700 cursor-pointer" />
        </div>
      </TableCell>
      <TableCell>
        <div className="w-16 mx-auto" onClick={deleteHandler}>
          {loading ? (
            <Loading size={16} className="!text-white" />
          ) : (
            <DeleteIcon
              fontSize="small"
              className="text-red-700 cursor-pointer"
            />
          )}
        </div>
      </TableCell>
      <TableCell>
        <Button
          text="درخواست برداشت"
          onClick={() =>
            withdrawRequest(row.id).then((resp) => {
              if (resp.data) {
                toast.success(resp.msg);
                onAction();
              } else toast.error(resp.msg);
            })
          }
          variant="contained"
          disabled={row.connectionStatus !== 1}
        />
      </TableCell>
      <TableCell>
        <Button
          text="لیست"
          onClick={() => setIsLogsModalOpen(true)}
          variant="contained"
        />
      </TableCell>
    </>
  );
};

export const Additional: IAdditional<ILiquidityManagementTableReport> = {
  getHeader: AdditionalHeader,
  getRow: AdditionalCol,
};
